import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getHeroContent } from '@/services/heroService';
import { useLanguage } from '@/context/LanguageContext';

const Hero: React.FC = () => {
  const { language, tl } = useLanguage();
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);

  const { data: heroContent } = useQuery({
    queryKey: ['heroContent', 'home', language],
    queryFn: () => getHeroContent('home'),
  });

  const content = heroContent
    ? {
        background_image: heroContent.background_image || '',
        media_type: heroContent.media_type || 'image',
        video_url: heroContent.video_url || null,
        carousel_images: heroContent.carousel_images || [],
      }
    : null;

  useEffect(() => {
    if (content?.media_type === 'carousel' && content.carousel_images?.length > 1) {
      const interval = setInterval(() => {
        setCurrentCarouselIndex((prev) => (prev + 1) % content.carousel_images.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [content?.media_type, content?.carousel_images]);

  const renderBackgroundMedia = () => {
    if (!content) return null;

    switch (content.media_type) {
      case 'video':
        if (content.video_url) {
          return (
            <video className="w-full h-full object-cover" autoPlay muted loop playsInline>
              <source src={content.video_url} type="video/mp4" />
              {content.background_image ? (
                <img src={content.background_image} alt="" className="w-full h-full object-cover" />
              ) : null}
            </video>
          );
        }
        return content.background_image ? (
          <img src={content.background_image} alt="" className="w-full h-full object-cover" />
        ) : null;

      case 'carousel':
        if (content.carousel_images?.length > 0) {
          return (
            <div className="relative w-full h-full">
              {content.carousel_images.map((image: string, index: number) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    index === currentCarouselIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img src={image} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          );
        }
        return content.background_image ? (
          <img src={content.background_image} alt="" className="w-full h-full object-cover" />
        ) : null;

      case 'image':
      default:
        return content.background_image ? (
          <img src={content.background_image} alt="" className="w-full h-full object-cover" />
        ) : null;
    }
  };

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight * 0.92, behavior: 'smooth' });
  };

  return (
    <section className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-black">
      <div className="absolute inset-0 z-0">
        {content ? renderBackgroundMedia() : null}
        <div className="absolute inset-0 bg-black/25" />
      </div>

      <button
        type="button"
        onClick={scrollToContent}
        aria-label={tl('Défiler', 'Scroll down', 'Scorri')}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white/90 hover:text-white transition-colors animate-bounce"
      >
        <ChevronDown size={28} strokeWidth={1.25} />
      </button>
    </section>
  );
};

export default Hero;
